import { Model, Factory, belongsTo, trait } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randPastDate,
  randRecentDate,
  randNumber,
  randChanceBoolean,
} from '@ngneat/falso'
import type { MockServer, MirageServer } from './../server.js'
import {
  pageable,
  filterable,
  sortable,
  createHtmlParagraph,
  getExistingEntity,
  createErrorResponse,
} from './../utils.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'
import type { ConsultationLaboratoryReferralAttributes } from '@/composables/consultationLaboratoryReferrals.js'

export const model = (
  Model as ModelDefinition<ConsultationLaboratoryReferralAttributes>
).extend({
  consultation: belongsTo(),
  department: belongsTo(),
  patient: belongsTo(),
  internalStatus: belongsTo('listItem'),
  sampler: belongsTo('user'),
  referringVeterinarian: belongsTo('user'),
  createdUser: belongsTo('user'),
  modifiedUser: belongsTo('user'),
  referralRemarksUser: belongsTo('user'),
  laboratoryRemarksUser: belongsTo('user'),
  internalNotesUser: belongsTo('user'),
})

export const factory = Factory.extend<ConsultationLaboratoryReferralAttributes>(
  {
    referral_type() {
      return rand(['CLINICAL_LABORATORY', 'MICROBIOLOGY', 'PATHOLOGY'])
    },
    laboratory_type() {
      return rand(['INTERNAL', 'EXTERNAL'])
    },
    status() {
      return rand(['DRAFT', 'IN_PROGRESS', 'COMPLETE', 'ARCHIVED'])
    },
    urgent() {
      return randChanceBoolean({ chanceTrue: 0.2 })
    },
    sample_taken() {
      return randChanceBoolean({ chanceTrue: 0.6 })
        ? randPastDate().toISOString()
        : null
    },
    planned_sample_time() {
      return randChanceBoolean({ chanceTrue: 0.8 })
        ? randRecentDate().toISOString()
        : null
    },
    created() {
      return randPastDate().toISOString()
    },
    modified() {
      return randRecentDate().toISOString()
    },
    referral_remarks() {
      return randChanceBoolean({ chanceTrue: 0.7 })
        ? createHtmlParagraph()
        : null
    },
    referral_remarks_modified() {
      return randChanceBoolean({ chanceTrue: 0.5 })
        ? randRecentDate().toISOString()
        : null
    },
    laboratory_remarks() {
      return randChanceBoolean({ chanceTrue: 0.6 })
        ? createHtmlParagraph()
        : null
    },
    laboratory_remarks_modified() {
      return randChanceBoolean({ chanceTrue: 0.4 })
        ? randRecentDate().toISOString()
        : null
    },
    internal_notes() {
      return randChanceBoolean({ chanceTrue: 0.5 })
        ? createHtmlParagraph()
        : null
    },
    internal_notes_modified() {
      return randChanceBoolean({ chanceTrue: 0.3 })
        ? randRecentDate().toISOString()
        : null
    },
    is_archivable() {
      return randChanceBoolean({ chanceTrue: 0.8 })
    },
    external_status() {
      return randChanceBoolean({ chanceTrue: 0.4 })
        ? rand(['NOT_SENT', 'SENDING', 'SENT', 'READY'])
        : null
    },
    external_type() {
      return randChanceBoolean({ chanceTrue: 0.3 })
        ? rand(['IDEXX', 'LABTERM', 'EXTLAB'])
        : null
    },
    archived_at() {
      return null
    },
    display_price() {
      return randNumber({ min: 50, max: 500, precision: 2 })
    },
    hidden_items_display_price() {
      return randChanceBoolean({ chanceTrue: 0.3 })
        ? randNumber({ min: 10, max: 100, precision: 2 })
        : 0
    },
    visible_panels_names() {
      const panelNames = [
        'Complete Blood Count',
        'Basic Metabolic Panel',
        'Liver Function Panel',
        'Thyroid Panel',
        'Lipid Panel',
        'Coagulation Panel',
      ]
      const count = randNumber({ min: 0, max: 4 })
      return count > 0 ? panelNames.slice(0, count) : []
    },
    visible_standalone_analyses_names() {
      const analysisNames = [
        'Glucose',
        'Creatinine',
        'BUN',
        'ALT',
        'AST',
        'Total Protein',
        'Albumin',
        'Globulin',
        'Calcium',
        'Phosphorus',
      ]
      const count = randNumber({ min: 0, max: 6 })
      return count > 0 ? analysisNames.slice(0, count) : []
    },
    any_row_has_results() {
      return randChanceBoolean({ chanceTrue: 0.4 })
    },
    are_results_editable() {
      return randChanceBoolean({ chanceTrue: 0.6 })
    },
    are_analyses_editable() {
      return randChanceBoolean({ chanceTrue: 0.8 })
    },
    laboratory_groups() {
      return rand(['IDEXX', 'Internal Lab', 'External Lab', 'University Lab'])
    },
    attachments() {
      const shouldHaveAttachments = randChanceBoolean({ chanceTrue: 0.8 })
      if (!shouldHaveAttachments) {
        return []
      }

      const attachmentCount = randNumber({ min: 1, max: 3 })
      const attachments = []

      const fileTypes = [
        { name: 'blood_test_results', ext: 'pdf' },
        { name: 'xray_lateral', ext: 'jpg' },
        { name: 'pathology_report', ext: 'pdf' },
        { name: 'lab_analysis', ext: 'pdf' },
        { name: 'ultrasound_image', ext: 'jpg' },
        { name: 'microscopy_sample', ext: 'jpg' },
      ]

      for (let i = 0; i < attachmentCount; i++) {
        const fileType = fileTypes[i % fileTypes.length]
        const isPdf = fileType.ext === 'pdf'

        // Use placeholder services for realistic file URLs
        const fileUrl = isPdf
          ? 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf'
          : `https://picsum.photos/800/600?random=${randNumber({ min: 1, max: 1000 })}`

        attachments.push({
          id: randNumber({ min: 1, max: 100000 }),
          name: `${fileType.name}.${fileType.ext}`,
          file_url: fileUrl,
          is_external: randChanceBoolean({ chanceTrue: 0.3 }),
        })
      }

      return attachments
    },

    withCreatedUser: trait({
      afterCreate(referral, server) {
        const createdUser = getExistingEntity(server, 'user')
        referral.update({ createdUser })
      },
    }),

    withModifiedUser: trait({
      afterCreate(referral, server) {
        const modifiedUser = getExistingEntity(server, 'user')
        referral.update({ modifiedUser })
      },
    }),

    withSampler: trait({
      afterCreate(referral, server) {
        const sampler = getExistingEntity(server, 'user')
        referral.update({ sampler })
      },
    }),

    withReferringVeterinarian: trait({
      afterCreate(referral, server) {
        const referringVeterinarian = getExistingEntity(server, 'user')
        referral.update({ referringVeterinarian })
      },
    }),

    afterCreate(referral, server) {
      if (!referral.consultation) {
        const consultation = getExistingEntity(server, 'consultation')
        referral.update({ consultation })
      }

      if (
        !referral.patient &&
        referral.consultation?.patients?.models?.length > 0
      ) {
        referral.update({ patient: referral.consultation.patients.models[0] })
      }

      if (!referral.department && referral.consultation?.department) {
        referral.update({ department: referral.consultation.department })
      }
    },
  },
)

export function routes(server: MockServer) {
  server.get(
    '/consultations/:parent_lookup_consultation/laboratory_referrals',
    function (this: MirageServer, schema, request) {
      const consultationId = request.params.parent_lookup_consultation
      const referrals = schema.where('consultationLaboratoryReferral', {
        consultationId,
      })

      const filtered = filterable(referrals, request, {
        status: (item, value) => item.attrs.status === value,
        laboratory_type: (item, value) => item.attrs.laboratory_type === value,
        referral_type: (item, value) => item.attrs.referral_type === value,
        urgent: (item, value) => item.attrs.urgent === (value === 'true'),
        is_active: (item, value) => {
          const isActive = !item.attrs.archived_at
          return value === 'false' ? !isActive : isActive
        },
      })

      const sorted = sortable(filtered, request, {}, ['-created'])

      const { data, meta } = pageable(sorted, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  server.patch(
    '/consultation_laboratory_referrals/:id/update_results',
    function (schema, request) {
      const body = JSON.parse(request.requestBody)
      const analyses = body.data?.relationships?.analyses?.data || []

      analyses.forEach(
        (analysis: {
          id: string
          attributes: { result?: string | null; remarks?: string | null }
        }) => {
          const existingAnalysis = schema.find(
            'consultationLaboratoryAnalysis',
            analysis.id,
          )

          if (existingAnalysis) {
            existingAnalysis.update({
              result: analysis.attributes.result,
              remarks: analysis.attributes.remarks,
              result_entered: analysis.attributes.result
                ? getTodayDateTimeISO()
                : null,
            })
          }
        },
      )

      return new Response(null, { status: 200 })
    },
  )

  server.patch(
    '/consultation_laboratory_referrals/:id/archive',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const referral = schema.find('consultationLaboratoryReferral', id)
      if (referral) {
        referral.update({ archived_at: new Date().toISOString() })
        referral.destroy()
        return referral
      }
      return createErrorResponse({
        status: 404,
        errors: [{ detail: 'Laboratory referral not found' }],
      })
    },
  )
}

export function seeds(server: MockServer) {
  const consultationIds = ['1', '2']

  consultationIds.forEach((id) => {
    const consultation = server.schema.find('consultation', id)

    if (consultation) {
      server.create(
        'consultationLaboratoryReferral',
        'withCreatedUser',
        'withModifiedUser',
        'withSampler',
        'withReferringVeterinarian',
        {
          consultation,
          patient: consultation.patients?.models?.[0] || null,
          department: consultation.department || null,
          any_row_has_results: true,
        },
      )

      server.create(
        'consultationLaboratoryReferral',
        'withCreatedUser',
        'withModifiedUser',
        'withSampler',
        'withReferringVeterinarian',
        {
          consultation,
          patient: consultation.patients?.models?.[0] || null,
          department: consultation.department || null,
          any_row_has_results: false,
        },
      )
    }
  })
}
